import {forwardRef, Ref, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import Button from './Button';
import VideoWithPoster from './VideoWithPoster';
import ICON_SOUND_OFF from '../../../public/static/v4/video/icon-sound-off.svg';
import ICON_SOUND_ON from '../../../public/static/v4/video/icon-sound-on.svg';
import useVideoAutoPlay, {IPosterType} from '../hook/useAutoPlayVideo';
import useValidVideoSources from '../hook/useValidVideoSources';
import {VideoSource} from '../type';

interface IProps extends Omit<React.HTMLProps<HTMLVideoElement>, 'autoPlay'> {
  shouldReplay?: boolean;
  sources: VideoSource[];
  isMute?: boolean;
  thresholdShow?: number;
  thresholdHide?: number;
  showMuteButton?: boolean;
  muteButtonClassName?: string;
  onPlay?: () => void;
  onClickMute?: (isMute: boolean) => void;
  onIntersection?: (isIntersection: boolean) => void;
  isPosterOnly?: boolean;
  posterType?: IPosterType;
  posterClassName?: string;
  posterHideDelay?: number;
}

function VideoAuto(
  {
    shouldReplay = false,
    sources,
    isMute = true,
    // 정확한 원인은 뭔지 잘 모르겠지만, 0.9로 하면 인터섹션 이벤트가 무시되는 경우가 있다.
    thresholdShow = 0.8,
    thresholdHide = 0,
    showMuteButton,
    muteButtonClassName,
    onPlay,
    onClickMute,
    onIntersection,
    isPosterOnly = false,
    posterType = 'background',
    posterClassName,
    posterHideDelay,
    ...rest
  }: IProps,
  ref: Ref<HTMLVideoElement>
) {
  const {t} = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const validSources = useValidVideoSources(sources);

  const {load, play, stop, showImgPoster} = useVideoAutoPlay({
    ref: videoRef,
    posterType,
    posterHideDelay,
    onPlay,
  });
  useEffect(() => {
    if (shouldReplay) {
      stop();
      play();
    }
  }, [stop, play, shouldReplay]);

  const onIntersectionRef = useRef(onIntersection);
  useEffect(() => {
    onIntersectionRef.current = onIntersection;
  });
  useEffect(() => {
    if (!validSources) return;

    const video = videoRef.current;
    if (!('IntersectionObserver' in window) || !video) return;

    function intersection(entries: IntersectionObserverEntry[]) {
      for (const entry of entries) {
        const el = entry.target as HTMLVideoElement;
        const isLoaded = !!el.dataset.loaded;
        if (!isLoaded && entry.intersectionRatio >= thresholdShow) {
          load();
          el.dataset.loaded = 'true';
          play();
          onIntersectionRef.current?.(true);
        } else if (isLoaded && entry.intersectionRatio <= thresholdHide) {
          el.dataset.loaded = '';
          stop();
          onIntersectionRef.current?.(false);
        }
      }
    }

    const observer = new IntersectionObserver(intersection, {
      threshold: [thresholdHide, thresholdShow],
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, [load, play, stop, thresholdHide, thresholdShow, validSources]);

  const element = (
    <VideoWithPoster
      posterType={posterType}
      posterClassName={posterClassName}
      ref={ref}
      videoRef={videoRef}
      validSources={validSources}
      showImgPoster={showImgPoster}
      isPosterOnly={isPosterOnly}
      {...rest}
    />
  );

  return (
    <>
      {showMuteButton && (
        <div className="w-full h-full relative">
          {element}
          <Button
            className={cn(
              'w-48 h-48',
              '!absolute bottom-6 right-6 z-2',
              muteButtonClassName
            )}
            onClick={e => {
              const video = videoRef.current;
              if (!video) return;
              video.muted = !isMute;
              onClickMute?.(!isMute);
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <img
              width="24"
              height="24"
              className={cn(isMute && 'hidden')}
              src={ICON_SOUND_ON}
              alt={t('sound_on')}
            />
            <img
              width="24"
              height="24"
              className={cn(!isMute && 'hidden')}
              src={ICON_SOUND_OFF}
              alt={t('sound_off')}
            />
          </Button>
        </div>
      )}
      {!showMuteButton && element}
    </>
  );
}

export default forwardRef(VideoAuto);
